/**
 * Warden (vanilla Java, normal difficulty): 500 HP, 0.9 x 2.9, speed 0.3, knockback resistance 1, immune to
 * fire / lava / drowning, never despawns while angry.
 *  - It is BLIND: no line-of-sight. Each player has an anger 0..150; once the top one is >= 80 it hunts that
 *    player. Anger: a vibration within 16 blocks +35 (a landing projectile +10), hitting it +100, touching it +35,
 *    and every 5-10 s of calm a sniff adds +35 to the nearest player within 6 horizontal / 20 vertical blocks.
 *    -1 per second. Sneaking players make no vibrations (entities/boss/wardenHooks.ts is the bus).
 *  - Melee 30 (shields are put down for 5 s), cooldown 18 ticks. Out of reach but within 15 horizontal / 20
 *    vertical: charges 34 ticks then a sonic boom (10, ignores armor / enchantments / blocks, knockback 2.5
 *    horizontal), cooldown 40 ticks.
 *  - Darkness (260 ticks) on survival players within 20 blocks every 120 ticks. Heartbeat quickens with anger.
 *  - Rises out of the ground (134 ticks, invulnerable); after 60 s with no anger and no vibration it digs down
 *    (100 ticks) and is gone.
 *  - Drops one sculk catalyst and 5 XP.
 */

import { SCULK_CATALYST_ID } from '../../blocks';
import { difficultyDamage } from '../../game/combat';
import { stackOf, type DamageKind, type MobType, type Stack } from '../../game/types';
import type { EntityHost, PlayerTarget } from '../host';
import { Mob, type MobCtx } from '../mob';
import { vibrationAnger, wardenHooks, type VibrationKind } from './wardenHooks';

export const WARDEN_HP = 500;
export const ANGRY_AT = 80;
export const ANGER_MAX = 150;
export const SENSE_RANGE = 16;
export const SNIFF_H = 6;
export const SNIFF_V = 20;
export const MELEE_DAMAGE = 30;
export const MELEE_COOLDOWN = 18;
export const BOOM_DAMAGE = 10;
export const BOOM_CHARGE = 34;
export const BOOM_COOLDOWN = 40;
export const BOOM_H = 15;
export const BOOM_V = 20;
export const EMERGE_TICKS = 134;
export const DIG_TICKS = 100;
/** 60 s with no anger and nothing sensed, and it digs back down. */
export const DIG_AFTER = 1200;
export const ROAR_TICKS = 84;
export const SNIFF_TICKS = 84;
export const DARKNESS_EVERY = 120;
export const DARKNESS_TICKS = 260;
export const DARKNESS_RANGE = 20;
const ATTACK_ANIM = 18;

export type WardenState = 'idle' | 'emerge' | 'dig' | 'sniff' | 'roar' | 'charge';
/** Codes carried in `view.color` bits 0-2 (progress 0..31 in bits 3-7); the model reads them. */
export const STATE_CODE: Record<WardenState, number> = { idle: 0, emerge: 1, dig: 2, sniff: 3, roar: 4, charge: 5 };

/** Heartbeat interval in ticks: 40 calm, 10 at full rage (vanilla). */
export const heartDelay = (topAnger: number): number => 40 - Math.floor(Math.min(1, Math.max(0, topAnger / ANGRY_AT)) * 30);

const valid = (p: PlayerTarget): boolean => p.alive && !p.creative && !p.spectator;

export class Warden extends Mob {
  state: WardenState = 'emerge';
  stateT = EMERGE_TICKS;
  /** Anger per player id ('' = the local player). */
  readonly anger = new Map<string, number>();
  /** Id of the player it is hunting (null = none). */
  targetId: string | null = null;
  /** Ticks without anger or vibration (digs away at DIG_AFTER). */
  calm = 0;
  readonly fireImmune = true;
  private atkCd = 0;
  private atkAnim = 0;
  private boomCd = 0;
  private touchCd = 0;
  private angerTick = 0;
  private heartCd = 0;
  private darkCd = 0;
  private sniffCd: number;
  private beat = 0;
  private disturb: { x: number; z: number; t: number } | null = null;
  private started = false;

  private readonly sink = (x: number, y: number, z: number, kind: VibrationKind, source: string | null): void => {
    if (this.removed) { wardenHooks.sinks.delete(this.sink); return; }
    this.onVibration(x, y, z, kind, source);
  };

  constructor(id: number, x: number, y: number, z: number, rnd: () => number) {
    super(id, 'warden' as MobType, x, y, z, { width: 0.9, height: 2.9, eye: 2.55, health: WARDEN_HP, speed: 0.3 }, rnd);
    this.knockbackResist = 1;
    this.persistent = true;
    this.sniffCd = 100 + Math.floor(rnd() * 100);
    wardenHooks.sinks.add(this.sink);
  }

  /** Skip the rising animation (tests, loaded saves). */
  skipEmerge(): void { if (this.state === 'emerge') { this.state = 'idle'; this.stateT = 0; } }

  get dug(): boolean { return this.state === 'emerge' || this.state === 'dig'; }

  override hurt(amount: number): boolean {
    if (this.dug) return false; // invulnerable while rising / sinking
    return super.hurt(amount);
  }

  override tick(ctx: MobCtx): void {
    const hp = this.health;
    const wasDying = this.dying;
    super.tick(ctx);
    // immune to lava and fire: undo what Mob.postTravel did this tick (a player's hit lands between ticks, never inside one)
    if ((this.inLava || this.fireTicks > 0) && this.health < hp && !wasDying) { this.health = hp; this.dying = false; }
    this.fireTicks = 0;
  }

  // ----------------------------------------------------------------- anger

  angerOf(id: string): number { return this.anger.get(id) ?? 0; }

  addAnger(id: string, n: number): void {
    this.anger.set(id, Math.min(ANGER_MAX, this.angerOf(id) + n));
    this.calm = 0;
  }

  /** Highest anger across players (drives the heartbeat). */
  get topAnger(): number {
    let t = 0;
    for (const a of this.anger.values()) if (a > t) t = a;
    return t;
  }

  /** A vibration anywhere: heard within 16 blocks, through walls. */
  onVibration(x: number, y: number, z: number, kind: VibrationKind, source: string | null): void {
    if (source === null || this.dug || this.dying || this.removed) return;
    const dx = x - this.x, dy = y - (this.y + this.height / 2), dz = z - this.z;
    if (dx * dx + dy * dy + dz * dz > SENSE_RANGE * SENSE_RANGE) return;
    const p = wardenHooks.players().find((q) => q.id === source);
    if (!p || !valid(p)) return;
    this.addAnger(source, vibrationAnger(kind));
    if (this.targetId === null) this.disturb = { x, z, t: 100 };
  }

  override onHurtBy(fromX: number, fromZ: number): void {
    // the attacker is the player standing where the blow came from
    let best: PlayerTarget | null = null, bd = 64;
    for (const p of wardenHooks.players()) {
      if (!valid(p)) continue;
      const d = (p.x - fromX) ** 2 + (p.z - fromZ) ** 2;
      if (d < bd) { bd = d; best = p; }
    }
    if (best) this.addAnger(best.id, 100);
    wardenHooks.sound('entity.warden.hurt', this.x, this.y + 1.5, this.z);
  }

  private tickAnger(players: readonly PlayerTarget[]): void {
    if (++this.angerTick < 20) return;
    this.angerTick = 0;
    for (const [id, a] of this.anger) {
      const p = players.find((q) => q.id === id);
      if (!p || !valid(p) || a <= 1) this.anger.delete(id);
      else this.anger.set(id, a - 1);
    }
  }

  private pickPrey(players: readonly PlayerTarget[]): PlayerTarget | null {
    let best: PlayerTarget | null = null, ba = ANGRY_AT - 1e-9;
    for (const p of players) {
      if (!valid(p)) continue;
      const a = this.angerOf(p.id);
      if (a > ba || (a === ba && p.id === this.targetId)) { ba = a; best = p; }
    }
    return best;
  }

  // -------------------------------------------------------------------- ai

  protected ai(ctx: MobCtx): void {
    if (!this.started) {
      this.started = true;
      if (this.state === 'emerge') wardenHooks.sound('entity.warden.emerge', this.x, this.y + 1, this.z);
    }
    if (this.atkCd > 0) this.atkCd--;
    if (this.atkAnim > 0) this.atkAnim--;
    if (this.boomCd > 0) this.boomCd--;
    if (this.touchCd > 0) this.touchCd--;
    this.fireTicks = 0;
    const players = wardenHooks.players();
    this.tickAnger(players);
    const top = this.topAnger;
    this.heldItem = Math.round(top);
    this.beat *= 0.8;
    this.swell = this.beat;

    // heartbeat (quickens with anger) and Darkness around it
    if (--this.heartCd <= 0) {
      this.heartCd = heartDelay(top);
      this.beat = 1;
      wardenHooks.sound('entity.warden.heartbeat', this.x, this.y + 1.8, this.z);
    }
    if (--this.darkCd <= 0) {
      this.darkCd = DARKNESS_EVERY;
      for (const p of players) {
        if (!valid(p)) continue;
        const dx = p.x - this.x, dy = p.y - this.y, dz = p.z - this.z;
        if (dx * dx + dy * dy + dz * dz <= DARKNESS_RANGE * DARKNESS_RANGE) wardenHooks.darkness(p, DARKNESS_TICKS);
      }
    }

    // rising / sinking: helpless
    if (this.state === 'emerge' || this.state === 'dig') {
      if (--this.stateT <= 0) {
        if (this.state === 'dig') { this.removed = true; return; }
        this.state = 'idle';
      }
      this.pack();
      return;
    }

    // touching it makes it angry (vanilla +35 every second)
    if (this.touchCd === 0) {
      for (const p of players) {
        if (!valid(p)) continue;
        if (Math.abs(p.x - this.x) < 0.9 && Math.abs(p.z - this.z) < 0.9 && p.y < this.y + this.height && p.y + p.height > this.y) {
          this.addAnger(p.id, 35);
          this.touchCd = 20;
          break;
        }
      }
    }

    const target = this.pickPrey(players);
    const had = this.targetId;
    this.targetId = target ? target.id : null;

    switch (this.state) {
      case 'roar':
        if (target) { this.faceToward(target.x, target.z); this.lookAt(target.x, target.y + target.height * 0.9, target.z); }
        if (--this.stateT <= 0) this.state = 'idle';
        break;
      case 'sniff':
        if (target) { this.state = 'roar'; this.stateT = ROAR_TICKS; wardenHooks.sound('entity.warden.roar', this.x, this.y + 2, this.z); break; }
        if (--this.stateT <= 0) { this.state = 'idle'; this.endSniff(players); }
        break;
      case 'charge':
        this.chargeBoom(ctx, target);
        break;
      default:
        if (target) this.fight(ctx, target, had === null);
        else this.idle(ctx);
    }

    // nothing happening for long enough: dig back down (never while angry or named)
    if ((this.state === 'idle' || this.state === 'sniff') && this.targetId === null && this.anger.size === 0 && !this.customName) {
      if (++this.calm >= DIG_AFTER) {
        this.state = 'dig'; this.stateT = DIG_TICKS;
        wardenHooks.sound('entity.warden.dig', this.x, this.y + 1, this.z);
      }
    } else if (this.targetId !== null) this.calm = 0;
    this.pack();
  }

  private idle(ctx: MobCtx): void {
    if (--this.sniffCd <= 0) {
      this.sniffCd = 100 + Math.floor(ctx.host.rnd() * 100); // 5-10 s
      this.state = 'sniff'; this.stateT = SNIFF_TICKS;
      wardenHooks.sound('entity.warden.sniff', this.x, this.y + 2, this.z);
      return;
    }
    const d = this.disturb;
    if (d) {
      if (--d.t <= 0 || Math.hypot(d.x - this.x, d.z - this.z) < 1.5) this.disturb = null;
      else { this.faceToward(d.x, d.z); this.steerTo(ctx, d.x, this.y, d.z, 0.8, 3); }
      return;
    }
    this.stroll(ctx, 0.5, 3, false);
  }

  /** End of a sniff: the nearest player within 6 horizontal / 20 vertical blocks gets +35 anger (sneaking or not). */
  private endSniff(players: readonly PlayerTarget[]): void {
    let best: PlayerTarget | null = null, bd = Infinity;
    for (const p of players) {
      if (!valid(p)) continue;
      const h = Math.hypot(p.x - this.x, p.z - this.z);
      if (h > SNIFF_H || Math.abs(p.y - this.y) > SNIFF_V) continue;
      if (h < bd) { bd = h; best = p; }
    }
    if (best) { this.addAnger(best.id, 35); this.disturb = { x: best.x, z: best.z, t: 60 }; }
  }

  private inReach(p: PlayerTarget): boolean {
    const dx = p.x - this.x, dz = p.z - this.z;
    const reach = this.width / 2 + p.width / 2 + 0.9;
    return dx * dx + dz * dz <= reach * reach && p.y < this.y + this.height && p.y + p.height > this.y;
  }

  private inBoomRange(p: PlayerTarget): boolean {
    return Math.hypot(p.x - this.x, p.z - this.z) <= BOOM_H && Math.abs(p.y - this.y) <= BOOM_V;
  }

  private fight(ctx: MobCtx, p: PlayerTarget, fresh: boolean): void {
    if (fresh) {
      // first sight (well, sound) of its prey: roar
      this.state = 'roar'; this.stateT = ROAR_TICKS;
      wardenHooks.sound('entity.warden.roar', this.x, this.y + 2, this.z);
      this.faceToward(p.x, p.z);
      return;
    }
    this.aggressive = true;
    this.faceToward(p.x, p.z);
    this.lookAt(p.x, p.y + p.height * 0.9, p.z);
    if (this.inReach(p)) {
      if (this.atkCd === 0) {
        this.atkCd = MELEE_COOLDOWN;
        this.atkAnim = ATTACK_ANIM;
        wardenHooks.sound('entity.warden.attack_impact', this.x, this.y + 1.5, this.z);
        this.hurtPlayer(ctx, p, MELEE_DAMAGE, 'melee', 1.5);
      }
      return;
    }
    if (this.boomCd === 0 && this.inBoomRange(p)) {
      this.state = 'charge'; this.stateT = BOOM_CHARGE;
      wardenHooks.sound('entity.warden.sonic_charge', this.x, this.y + 2, this.z);
      return;
    }
    // hunt it: path toward the player (no line of sight needed, it just knows where they are)
    this.steerTo(ctx, p.x, p.y, p.z, 1.2, 3);
  }

  private chargeBoom(ctx: MobCtx, p: PlayerTarget | null): void {
    this.aggressive = true;
    if (p) { this.faceToward(p.x, p.z); this.lookAt(p.x, p.y + p.height * 0.9, p.z); }
    if (--this.stateT > 0) return;
    this.state = 'idle';
    this.boomCd = BOOM_COOLDOWN;
    this.atkAnim = ATTACK_ANIM;
    if (!p || !valid(p) || !this.inBoomRange(p)) return;
    const ox = this.x, oy = this.y + 1.6, oz = this.z;
    const tx = p.x, ty = p.y + p.height * 0.9, tz = p.z;
    wardenHooks.sound('entity.warden.sonic_boom', ox, oy, oz);
    wardenHooks.fx.boom(ox, oy, oz, tx, ty, tz);
    // straight through blocks: no line-of-sight test
    if (!this.hurtPlayer(ctx, p, BOOM_DAMAGE, 'sonic_boom', 0)) return;
    const dx = tx - ox, dy = ty - oy, dz = tz - oz;
    const l = Math.hypot(dx, dy, dz) || 1;
    p.knockback(dx, dz, 2.5 * (Math.hypot(dx, dz) / l));
    wardenHooks.toss(p, 0.5 * (dy / l));
  }

  /** Difficulty-scaled hit on a player; false when nothing landed (peaceful, blocked, immune). */
  private hurtPlayer(ctx: MobCtx, p: PlayerTarget, amount: number, kind: DamageKind, kb: number): boolean {
    const dmg = difficultyDamage(amount, ctx.host.difficulty?.() ?? 'normal');
    if (dmg <= 0) return false;
    const r = p.damage(dmg, { kind, x: this.x, y: this.y, z: this.z, attackerId: this.id });
    if (r === 'blocked') return false;
    if (kb > 0) p.knockback(p.x - this.x, p.z - this.z, kb);
    return true;
  }

  /** color: state code (bits 0-2) + progress 0..31 (bits 3-7), the model's cue; heldItem carries the top anger. */
  private pack(): void {
    const dur = this.state === 'emerge' ? EMERGE_TICKS : this.state === 'dig' ? DIG_TICKS
      : this.state === 'sniff' ? SNIFF_TICKS : this.state === 'roar' ? ROAR_TICKS : this.state === 'charge' ? BOOM_CHARGE : 1;
    const prog = this.state === 'idle' ? 0 : Math.max(0, Math.min(31, Math.round(31 * (1 - this.stateT / dur))));
    this.color = STATE_CODE[this.state] | (prog << 3);
  }

  override syncView(host: EntityHost): void {
    super.syncView(host);
    if (this.atkAnim > 0) this.view.attackAnim = this.atkAnim / ATTACK_ANIM; // replicas keep the swing the network gave them
  }

  loot(_rnd: () => number, _byPlayer: boolean): Stack[] { return [stackOf(SCULK_CATALYST_ID, 1)]; }
  xpValue(): number { return 5; }

  saveExtra(): unknown {
    return { s: STATE_CODE[this.state], t: this.stateT, c: this.calm, a: [...this.anger] };
  }
  loadExtra(d: unknown): void {
    const o = d as { s?: number; t?: number; c?: number; a?: unknown };
    if (!o || typeof o !== 'object') return;
    const names = Object.keys(STATE_CODE) as WardenState[];
    const st = typeof o.s === 'number' ? names.find((n) => STATE_CODE[n] === o.s) : undefined;
    this.state = st && st !== 'charge' && st !== 'roar' && st !== 'sniff' ? st : 'idle';
    this.stateT = typeof o.t === 'number' && this.state !== 'idle' ? Math.max(1, Math.min(EMERGE_TICKS, o.t)) : 0;
    this.calm = typeof o.c === 'number' ? o.c : 0;
    this.started = true;
    this.anger.clear();
    if (Array.isArray(o.a)) {
      for (const e of o.a) {
        if (Array.isArray(e) && typeof e[0] === 'string' && typeof e[1] === 'number' && e[1] > 0) this.anger.set(e[0], Math.min(ANGER_MAX, e[1]));
      }
    }
  }
}

/** Factory for EntityManager.spawnMob's fallback chain. */
export function createWardenMob(type: MobType, id: number, x: number, y: number, z: number, rnd: () => number): Mob | null {
  return type === ('warden' as MobType) ? new Warden(id, x, y, z, rnd) : null;
}
